/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useCallback, useEffect } from 'react';
import { getVertexAuthToken } from '../firebase';
import { createBlob, decodeAudioData, decode, getUserFriendlyErrorMessage } from '../utils';

const audioWorkletCode = `
class PCMProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.bufferSize = 4096;
    this.buffer = new Float32Array(this.bufferSize);
    this.index = 0;
  }

  process(inputs, outputs, parameters) {
    const input = inputs[0];
    if (input && input.length > 0) {
      const channelData = input[0];
      for (let i = 0; i < channelData.length; i++) {
        this.buffer[this.index++] = channelData[i];
        if (this.index >= this.bufferSize) {
          this.port.postMessage(this.buffer.slice());
          this.index = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('pcm-processor', PCMProcessor);
`;

// Noise gate thresholds (16kHz, 4096-sample frames ≈ 256ms per frame)
const IDLE_RMS_THRESHOLD = 0.012;
const IDLE_PEAK_THRESHOLD = 0.045;
// Higher threshold while AI customer audio is actively playing to prevent speaker bleed / background noise from triggering false barge-in
const BARGE_IN_RMS_THRESHOLD = 0.035;
const BARGE_IN_PEAK_THRESHOLD = 0.10;
// Keep gate open for N frames (~768ms) after voice drops below threshold so trailing syllables & brief pauses aren't clipped
const SPEECH_HANGOVER_FRAMES = 3;

interface UseLiveSessionProps {
    onMessage: (speaker: 'user' | 'customer' | 'system', text: string, isStreaming: boolean) => void;
    onTurnComplete: () => void;
    onError: (error: string) => void;
    onDisconnect: () => void;
}

export const useLiveSession = ({ onMessage, onTurnComplete, onError, onDisconnect }: UseLiveSessionProps) => {
    const [isActive, setIsActive] = useState(false);
    const isActiveRef = useRef(false);
    const [isMuted, setIsMuted] = useState(false);
    const isMutedRef = useRef(false);
    const [status, setStatus] = useState<string>('');

    const webSocket = useRef<WebSocket | null>(null);
    const inputAudioContext = useRef<AudioContext | null>(null);
    const outputAudioContext = useRef<AudioContext | null>(null);
    const microphoneStream = useRef<MediaStream | null>(null);
    const audioWorkletNode = useRef<AudioWorkletNode | null>(null);
    const mediaStreamSource = useRef<MediaStreamAudioSourceNode | null>(null);
    const filterNodesRef = useRef<AudioNode[]>([]);
    const audioPlaybackSources = useRef<Set<AudioBufferSourceNode>>(new Set());
    const nextAudioPlaybackStartTime = useRef<number>(0);
    const speechHangoverChunksRef = useRef<number>(0);

    const currentInputTranscription = useRef('');
    const currentOutputTranscription = useRef('');
    const disableTranscriptionRef = useRef(false);
    const disableVadConfigRef = useRef(false);

    const cleanup = useCallback(() => {
        console.log("Cleaning up Vertex AI live session resources...");
        microphoneStream.current?.getTracks().forEach(track => track.stop());

        if (audioWorkletNode.current) {
            audioWorkletNode.current.port.onmessage = null;
            audioWorkletNode.current.disconnect();
        }

        for (const node of filterNodesRef.current) {
            try { node.disconnect(); } catch { /* ignore */ }
        }
        filterNodesRef.current = [];

        mediaStreamSource.current?.disconnect();

        if (inputAudioContext.current && inputAudioContext.current.state !== 'closed') {
            inputAudioContext.current.close().catch(e => console.warn("Error closing input audio context", e));
        }
        if (outputAudioContext.current && outputAudioContext.current.state !== 'closed') {
            outputAudioContext.current.close().catch(e => console.warn("Error closing output audio context", e));
        }

        for (const source of audioPlaybackSources.current) {
            try { source.stop(); } catch { /* ignore */ }
        }
        audioPlaybackSources.current.clear();

        if (webSocket.current) {
            webSocket.current.onmessage = null;
            webSocket.current.onclose = null;
            webSocket.current.onerror = null;
            webSocket.current.close();
            webSocket.current = null;
        }

        isActiveRef.current = false;
        setIsActive(false);
        inputAudioContext.current = null;
        outputAudioContext.current = null;
        microphoneStream.current = null;
        audioWorkletNode.current = null;
        mediaStreamSource.current = null;
        nextAudioPlaybackStartTime.current = 0;
        speechHangoverChunksRef.current = 0;
        isMutedRef.current = false;
        setIsMuted(false);
        console.log("Vertex AI live session resources cleaned up.");
    }, []);

    const stopSession = useCallback(async () => {
        setStatus('Disconnecting...');
        cleanup();
        onDisconnect();
        setStatus('Call Ended');
    }, [cleanup, onDisconnect]);

    const toggleMute = useCallback(() => {
        if (!microphoneStream.current) return;
        const newMutedState = !isMuted;
        isMutedRef.current = newMutedState;
        setIsMuted(newMutedState);
        microphoneStream.current.getAudioTracks().forEach(track => {
            track.enabled = !newMutedState;
        });
    }, [isMuted]);

    const sendTextMessage = useCallback((text: string) => {
        if (text.includes('[REQUEST_TONE_SUMMARY]')) {
            // Mute microphone input while requesting post-call tone summary so ambient audio cannot interrupt the JSON response
            isMutedRef.current = true;
        }
        if (webSocket.current && webSocket.current.readyState === WebSocket.OPEN) {
            const message = {
                clientContent: {
                    turns: [{
                        role: "user",
                        parts: [{ text: text }]
                    }],
                    turnComplete: true
                }
            };
            webSocket.current.send(JSON.stringify(message));
        }
    }, []);

    const startSession = useCallback(async (systemPrompt: string, gender: 'male' | 'female', _languageCode: string = 'en-US') => {
        if (isActiveRef.current) return;
        cleanup(); // Clear any stale resources before starting new ones

        isActiveRef.current = true;
        setIsActive(true);
        disableTranscriptionRef.current = false; // Reset fallback on new session
        disableVadConfigRef.current = false;
        setStatus('Initializing Vertex session...');

        let authToken: string;
        try {
            console.log("Fetching Vertex AI Auth Token...");
            const result: any = await getVertexAuthToken();
            authToken = result.data.token;
            if (!authToken) throw new Error("Auth token was not returned from the backend.");
        } catch (err) {
            console.error("Failed to fetch Vertex AI Auth Token:", err);
            const errorMsg = `Could not start voice call. Failed to retrieve security credentials. ${getUserFriendlyErrorMessage(err)}`;
            onError(errorMsg);
            isActiveRef.current = false;
            setIsActive(false);
            setStatus('Auth Failed');
            return;
        }

        const voiceName = (gender === 'male') ? 'Charon' : 'Aoede';
        const projectNumber = "993966945157"; // Project number for simulate2nl

        const CONFIGS = [
            { region: 'us-central1', model: 'gemini-live-2.5-flash-native-audio' },
            { region: 'us-central1', model: 'gemini-3.8-live' }
        ];

        let configIndex = 0;

        const connect = async () => {
            if (configIndex >= CONFIGS.length) {
                setStatus('All Connection Paths Failed');
                onError("Vertex AI could not be reached. Ensure the Generative AI API is enabled and billing is active. Check console for details.");
                stopSession();
                return;
            }

            const { region, model } = CONFIGS[configIndex];
            const currentStatus = `Connecting to ${model} (${region})... [Path ${configIndex + 1}/${CONFIGS.length}]`;
            console.log(currentStatus);
            setStatus(currentStatus);

            try {
                if (webSocket.current) {
                    webSocket.current.onclose = null;
                    webSocket.current.close();
                }

                const wsUrl = `wss://${region}-aiplatform.googleapis.com/ws/google.cloud.aiplatform.v1beta1.LlmBidiService/BidiGenerateContent?access_token=${authToken}`;
                webSocket.current = new WebSocket(wsUrl);

                // Function to start audio streaming AFTER setup is complete
                const startAudioStreaming = async () => {
                    console.log("Starting Audio Streaming...");
                    if (!audioWorkletNode.current && inputAudioContext.current && microphoneStream.current) {
                        try {
                            const ctx = inputAudioContext.current;
                            // Resume context if suspended (common browser policy restriction)
                            if (ctx.state === 'suspended') {
                                try {
                                    await ctx.resume();
                                    console.log("Input Audio Context resumed.");
                                } catch (e) {
                                    console.warn("Failed to resume input audio context:", e);
                                }
                            }

                            mediaStreamSource.current = ctx.createMediaStreamSource(microphoneStream.current);

                            // Voice band-pass filtering to strip low-frequency rumble (AC hum, desk thumps < 85Hz)
                            // and high-frequency hiss/clicks (> 3800Hz) before PCM encoding
                            const highPassFilter = ctx.createBiquadFilter();
                            highPassFilter.type = 'highpass';
                            highPassFilter.frequency.value = 85;
                            highPassFilter.Q.value = 0.707;

                            const lowPassFilter = ctx.createBiquadFilter();
                            lowPassFilter.type = 'lowpass';
                            lowPassFilter.frequency.value = 3800;
                            lowPassFilter.Q.value = 0.707;

                            filterNodesRef.current = [highPassFilter, lowPassFilter];

                            const blob = new Blob([audioWorkletCode], { type: "application/javascript" });
                            const workletUrl = URL.createObjectURL(blob);

                            // Add module and create node
                            await ctx.audioWorklet.addModule(workletUrl);
                            URL.revokeObjectURL(workletUrl);
                            audioWorkletNode.current = new AudioWorkletNode(ctx, 'pcm-processor');

                            audioWorkletNode.current.port.onmessage = (event) => {
                                if (webSocket.current?.readyState !== WebSocket.OPEN || !isActiveRef.current) return;

                                const inputData = event.data as Float32Array;
                                const isAiCurrentlySpeaking = audioPlaybackSources.current.size > 0;

                                // Compute RMS energy and peak amplitude to filter out background noise
                                let sumSquares = 0;
                                let peak = 0;
                                for (let i = 0; i < inputData.length; i++) {
                                    const sample = inputData[i];
                                    const abs = sample < 0 ? -sample : sample;
                                    sumSquares += sample * sample;
                                    if (abs > peak) peak = abs;
                                }
                                const rms = Math.sqrt(sumSquares / inputData.length);

                                const rmsThreshold = isAiCurrentlySpeaking ? BARGE_IN_RMS_THRESHOLD : IDLE_RMS_THRESHOLD;
                                const peakThreshold = isAiCurrentlySpeaking ? BARGE_IN_PEAK_THRESHOLD : IDLE_PEAK_THRESHOLD;

                                let frameToSend: Float32Array;

                                if (isMutedRef.current) {
                                    speechHangoverChunksRef.current = 0;
                                    frameToSend = new Float32Array(inputData.length);
                                } else if (rms >= rmsThreshold && peak >= peakThreshold) {
                                    // Clear foreground speech detected — open noise gate and reset hangover counter
                                    speechHangoverChunksRef.current = SPEECH_HANGOVER_FRAMES;
                                    frameToSend = inputData;
                                } else if (!isAiCurrentlySpeaking && speechHangoverChunksRef.current > 0) {
                                    // Hangover window after active speech so trailing consonants and natural word pauses aren't clipped
                                    speechHangoverChunksRef.current--;
                                    frameToSend = inputData;
                                } else {
                                    // Background noise floor only — send digital silence so server VAD timing advances
                                    // without triggering false user turns on room noise or distant chatter
                                    frameToSend = new Float32Array(inputData.length);
                                }

                                const pcmData = createBlob(frameToSend);

                                // Vertex AI v1beta1 REQUIRES snake_case: realtime_input
                                const bidiMessage = {
                                    realtime_input: {
                                        media_chunks: [{
                                            data: pcmData.data,
                                            mime_type: pcmData.mimeType
                                        }]
                                    }
                                };
                                webSocket.current.send(JSON.stringify(bidiMessage));
                            };

                            mediaStreamSource.current.connect(highPassFilter);
                            highPassFilter.connect(lowPassFilter);
                            lowPassFilter.connect(audioWorkletNode.current);
                            audioWorkletNode.current.connect(ctx.destination);
                            console.log("Audio Worklet connected with voice band-pass filter and noise gate.");
                            setStatus('Call Active'); // Only set status to Active once audio is flowing
                        } catch (err) {
                            console.error("Error starting audio streaming in worklet:", err);
                            setStatus('Audio Error');
                        }
                    }
                };

                webSocket.current.onopen = async () => {
                    console.log(`WebSocket Connected: ${model}`);
                    setStatus('Connecting...'); // Keeping status as connecting until setup completes

                    // Explicitly resume output audio context just in case
                    if (outputAudioContext.current?.state === 'suspended') await outputAudioContext.current.resume();

                    const setupMessage = {
                        setup: {
                            model: `projects/${projectNumber}/locations/${region}/publishers/google/models/${model}`,
                            generation_config: {
                                response_modalities: ["AUDIO"],
                                speech_config: {
                                    voice_config: {
                                        prebuilt_voice_config: { voice_name: voiceName },
                                    },
                                },
                            },
                            ...(!disableVadConfigRef.current && {
                                realtime_input_config: {
                                    automatic_activity_detection: {
                                        disabled: false,
                                        silence_duration_ms: 500,
                                        start_of_speech_sensitivity: "START_SENSITIVITY_LOW",
                                        end_of_speech_sensitivity: "END_SENSITIVITY_HIGH",
                                    },
                                },
                            }),
                            system_instruction: {
                                parts: [{ text: systemPrompt }]
                            },
                            // Vertex AI v1beta1: Use these for simultaneous audio/text
                            ...(!disableTranscriptionRef.current && {
                                input_audio_transcription: {},
                                output_audio_transcription: {}
                            })
                        },
                    };
                    console.log(
                        `Sending Setup Message (Transcription: ${!disableTranscriptionRef.current ? 'ON' : 'OFF'}, VAD Low-Sensitivity: ${!disableVadConfigRef.current ? 'ON' : 'OFF'}):`,
                        setupMessage
                    );
                    webSocket.current?.send(JSON.stringify(setupMessage));
                };

                webSocket.current.onmessage = async (event) => {
                    let textData = '';
                    if (event.data instanceof Blob) {
                        textData = await event.data.text();
                    } else if (typeof event.data === 'string') {
                        textData = event.data;
                    }

                    let message;
                    try {
                        message = JSON.parse(textData);
                        console.log("Vertex AI Received:", message);
                    } catch {
                        console.error("Failed to parse Vertex AI message:", event.data);
                        return;
                    }

                    if (message.error) {
                        console.error("Vertex AI WebSocket Error:", message.error);
                        if (message.error.code === 404 || message.error.message?.includes("not found")) {
                            console.warn("Retrying with next config due to 404...");
                            webSocket.current?.close(4000, "RetryNext");
                            return;
                        }
                    }

                    if (message.setupComplete || message.setup_complete) {
                        console.log("Vertex AI Setup acknowledged by server:", message.setupComplete || message.setup_complete);
                        setStatus('Server Ready');
                        // Start audio streaming only after server confirms setup
                        await startAudioStreaming();
                    }

                    const serverContent = message.serverContent || message.server_content;

                    if (serverContent?.inputTranscription || serverContent?.input_transcription) {
                        const inputTranscription = serverContent.inputTranscription || serverContent.input_transcription;
                        const text = inputTranscription.text || "";
                        if (text) {
                            currentInputTranscription.current += text;
                            onMessage('user', currentInputTranscription.current, true);
                        }
                    }
                    if (serverContent?.outputTranscription || serverContent?.output_transcription) {
                        const outputTranscription = serverContent.outputTranscription || serverContent.output_transcription;
                        const text = outputTranscription.text || "";
                        if (text) {
                            // If AI starts speaking while user input transcription is still uncommitted, commit user turn first
                            if (currentInputTranscription.current.trim()) {
                                onMessage('user', currentInputTranscription.current.trim(), false);
                                currentInputTranscription.current = '';
                            }
                            currentOutputTranscription.current += text;
                            onMessage('customer', currentOutputTranscription.current, true);
                        }
                    }

                    if (serverContent?.turnComplete || serverContent?.turn_complete) {
                        if (currentInputTranscription.current.trim()) onMessage('user', currentInputTranscription.current, false);
                        if (currentOutputTranscription.current.trim()) onMessage('customer', currentOutputTranscription.current, false);
                        currentInputTranscription.current = '';
                        currentOutputTranscription.current = '';
                        onTurnComplete();
                    }

                    const modelTurn = serverContent?.modelTurn || serverContent?.model_turn;
                    if (modelTurn) {
                        // Commit pending user transcription before AI model turn plays
                        if (currentInputTranscription.current.trim()) {
                            onMessage('user', currentInputTranscription.current.trim(), false);
                            currentInputTranscription.current = '';
                        }
                    }

                    if (modelTurn?.parts) {
                        for (const part of modelTurn.parts) {
                            if (part.text) {
                                currentOutputTranscription.current += part.text;
                                onMessage('customer', currentOutputTranscription.current, true);
                            }
                            const inlineData = part.inlineData || part.inline_data;
                            if (inlineData?.data && outputAudioContext.current) {
                                const base64Audio = inlineData.data;
                                nextAudioPlaybackStartTime.current = Math.max(nextAudioPlaybackStartTime.current, outputAudioContext.current.currentTime);
                                const audioBuffer = await decodeAudioData(decode(base64Audio), outputAudioContext.current, 24000, 1);
                                const source = outputAudioContext.current.createBufferSource();
                                source.buffer = audioBuffer;
                                source.connect(outputAudioContext.current.destination);
                                source.addEventListener('ended', () => {
                                    audioPlaybackSources.current.delete(source);
                                });
                                source.start(nextAudioPlaybackStartTime.current);
                                nextAudioPlaybackStartTime.current += audioBuffer.duration;
                                audioPlaybackSources.current.add(source);
                            }
                        }
                    }

                    if (serverContent?.interrupted) {
                        if (currentOutputTranscription.current.trim()) {
                            onMessage('customer', currentOutputTranscription.current.trim(), false);
                            currentOutputTranscription.current = '';
                        }
                        for (const source of audioPlaybackSources.current) {
                            try { source.stop(); } catch { /* ignore */ }
                        }
                        audioPlaybackSources.current.clear();
                        nextAudioPlaybackStartTime.current = 0;
                    }
                };

                webSocket.current.onerror = (e: any) => {
                    console.error('Vertex AI WebSocket error:', e);
                };

                webSocket.current.onclose = (event) => {
                    const reason = event.reason || 'Network Close';
                    console.log(`WebSocket Closed [${event.code}]: ${reason}`);

                    // Fallback if server rejects realtime_input_config / automatic_activity_detection
                    if ((event.code === 1007 || reason.includes("Invalid JSON payload")) &&
                        (reason.includes("realtime_input_config") || reason.includes("automatic_activity_detection")) &&
                        !disableVadConfigRef.current) {
                        console.warn("Server rejected VAD config. Retrying without realtime_input_config...");
                        disableVadConfigRef.current = true;
                        setTimeout(() => { if (isActiveRef.current) connect(); }, 300);
                        return;
                    }

                    // Specific fallback for "Unknown name" errors in setup (transcription config)
                    if ((event.code === 1007 || reason.includes("Invalid JSON payload")) &&
                        (reason.includes("input_audio_transcription") || reason.includes("inputTranscriptionConfig")) &&
                        !disableTranscriptionRef.current) {
                        console.warn("Server rejected transcription config. Retrying without transcription...");
                        disableTranscriptionRef.current = true;
                        setTimeout(() => { if (isActiveRef.current) connect(); }, 300);
                        return;
                    }

                    const isFailure = event.code === 4000 || event.code === 1007 || event.code === 1008 || reason.includes("RetryNext") || (event.code === 1006 || (event.code === 1000 && status !== 'Call Active'));

                    if (isFailure && configIndex < CONFIGS.length - 1 && isActiveRef.current) {
                        configIndex++;
                        disableTranscriptionRef.current = false;
                        disableVadConfigRef.current = false;
                        setTimeout(() => { if (isActiveRef.current) connect(); }, 500);
                    } else if (isActiveRef.current && status !== 'Call Ended') {
                        stopSession();
                    }
                };
            } catch (err: any) {
                console.error("Failed to initiate WebSocket connection:", err);
                if (configIndex < CONFIGS.length - 1) {
                    configIndex++;
                    connect();
                } else {
                    onError(`Connection failed: ${err.message}`);
                    stopSession();
                }
            }
        };

        try {
            inputAudioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
            outputAudioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });

            const preferredAudioConstraints: MediaTrackConstraints = {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                channelCount: 1,
                sampleRate: 16000,
            };

            try {
                microphoneStream.current = await navigator.mediaDevices.getUserMedia({ audio: preferredAudioConstraints });
            } catch (mediaErr: any) {
                if (mediaErr.name === 'NotReadableError') {
                    console.warn("Microphone temporarily locked (NotReadableError). Retrying in 1 second...");
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    microphoneStream.current = await navigator.mediaDevices.getUserMedia({ audio: preferredAudioConstraints });
                } else if (mediaErr.name === 'OverconstrainedError' || mediaErr.name === 'ConstraintNotSatisfiedError') {
                    console.warn("Preferred audio constraints not supported by device, falling back to basic noise suppression...");
                    microphoneStream.current = await navigator.mediaDevices.getUserMedia({
                        audio: { echoCancellation: true, noiseSuppression: true }
                    });
                } else {
                    throw mediaErr;
                }
            }

            await connect();

        } catch (err: any) {
            console.error("Failed to start Vertex AI session:", err);
            const errorMsg = `Failed to start voice call. ${getUserFriendlyErrorMessage(err)}`;
            onError(errorMsg);
            isActiveRef.current = false;
            setIsActive(false);
            setStatus('Call Failed');
        }
    }, [cleanup, onMessage, onTurnComplete, onError, stopSession]);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return {
        isActive,
        isMuted,
        status,
        startSession,
        stopSession,
        toggleMute,
        sendTextMessage
    };
};
